// Global error handling (must be first!)
import { setupGlobalErrorHandling } from './app/error-catcher';
setupGlobalErrorHandling();

// SCRUM-1962 — Tesla in-car browser: flag the document root before Angular
// boots so the car-optimized token scale (:root.tesla) applies on every page,
// login included. Heuristic UA match (Chromium `Tesla/<ver>` or legacy
// `QtCarBrowser`); presentation-only, never a security gate.
// Auto-detect the Tesla in-car browser from the User-Agent, but ALSO honor a
// persisted manual switch — `?carmode=1` turns it on (and sticks), `?carmode=0`
// turns it off — so large-car-text mode can be forced on cars whose browser UA
// does not advertise "Tesla" (we can't verify the UA for every model/firmware).
if (typeof navigator !== 'undefined' && typeof document !== 'undefined') {
  const ua = navigator.userAgent || '';
  const uaTesla = /\bTesla\b|QtCarBrowser/i.test(ua);
  let forced = false;
  let disabled = false;
  try {
    const p = new URLSearchParams(location.search);
    if (p.get('carmode') === '1') localStorage.setItem('hub_car_mode', '1');
    if (p.get('carmode') === '0') localStorage.removeItem('hub_car_mode');
    forced = localStorage.getItem('hub_car_mode') === '1';
    disabled = p.get('carmode') === '0';
  } catch { /* URL/storage unavailable — fall back to UA only */ }

  // SCRUM-2260 — characteristics fallback: many Tesla firmwares report a
  // Chromium UA that does NOT contain "Tesla", so the UA test alone misses.
  // A large landscape touch screen with NO mouse, on a non-phone/tablet/desktop
  // OS, is almost certainly an in-dash car browser (Tesla's is Linux/X11
  // Chromium). Exclude iPhone/iPad/Android/Mac/Windows/CrOS so iPads, Android
  // tablets and touch laptops are NOT swept in. `?carmode=0` still overrides.
  let carLike = false;
  try {
    const notPhoneTabletDesktop = !/iPhone|iPad|iPod|Android|Macintosh|Windows|CrOS/i.test(ua);
    const mq = (q: string) => window.matchMedia(q).matches;
    // SCRUM-2296 — the 2024-25 Tesla firmware update broke the old gates: the
    // in-car browser now reports `pointer: fine` + `hover: hover` and, with
    // device-pixel-ratio raised 1.0→1.53, a narrower CSS viewport. The old
    // coarse-pointer + no-fine-pointer + wide-floor gates therefore never
    // matched → car mode stopped auto-activating.
    // Use a touch-ish signal a normal mouse desktop can NEVER satisfy (coarse
    // pointer OR no hover), landscape, and a lower width floor. A desktop
    // (hover: hover AND fine pointer) fails `touchish`, so it is not swept in.
    // NOTE: a fully desktop-like car (hover:hover + fine pointer) will still miss
    // here — the visible toggle / `?carmode=1` is the reliable fallback until the
    // real in-car User-Agent is captured to add a UA fast-path.
    const touchish = mq('(pointer: coarse)') || mq('(hover: none)');
    carLike =
      notPhoneTabletDesktop &&
      touchish &&
      mq('(orientation: landscape)') &&
      mq('(min-width: 768px)');
  } catch { /* matchMedia unavailable — skip the fallback */ }

  if (!disabled && (uaTesla || forced || carLike)) {
    document.documentElement.classList.add('tesla');
    // SCRUM-2261 — apply the user's saved car text size pre-boot so there's no
    // flash of the default. The in-car A-/A+ control writes `hub_car_scale`.
    try {
      const s = localStorage.getItem('hub_car_scale');
      if (s) document.documentElement.style.setProperty('--car-scale', `${s}%`);
    } catch { /* storage unavailable */ }
  }
}

import { environment } from './environments/environment';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withPreloading, NoPreloading, Router } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations'; // NOSONAR S1874 — Angular 21 marks provideAnimations deprecated but provideAnimationsAsync changes loading behavior; keeping sync version intentionally
import { provideHttpClient, withInterceptors, HTTP_INTERCEPTORS } from '@angular/common/http'; // NOSONAR S3863 — intentional split imports
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const AlphaGrovesPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50:  '{green.50}',
      100: '#d1f5e3',
      200: '#a3ebb0',
      300: '#6dd98a',
      400: '#27C07C',
      500: '#0B6A43',
      600: '#085232',
      700: '#064228',
      800: '#04311e',
      900: '#022114',
      950: '#011008',
    },
  },
});
import { APP_INITIALIZER, ErrorHandler, Injectable, Provider } from '@angular/core'; // NOSONAR S1874 — APP_INITIALIZER used for Sentry init; no alternative available in this Angular version
import type * as SentryType from '@sentry/angular';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { authInterceptorFn } from './app/interceptors/auth-functional.interceptor';
import { PublicConfigService } from './app/services/public-config.service';

// Request logging interceptor
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http'; // NOSONAR S3863 — intentional split imports
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class LoggingInterceptor implements HttpInterceptor {
  // Only log genuine problems. The previous version logged every request and
  // response which drowned the console. Routine 401s (unauth before login)
  // and 4xx that the app handles via catchError don't need to surface; only
  // 5xx, 0 (network failure), and unknown errors do.
  private static readonly SHOULD_LOG = (status: number) => status === 0 || status >= 500;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const startTime = performance.now();
    return next.handle(req).pipe(
      catchError((error) => {
        if (LoggingInterceptor.SHOULD_LOG(error?.status)) {
          const duration = (performance.now() - startTime).toFixed(2);
          console.error('[HTTP]', error?.status ?? 'network', req.method, req.url, `(${duration}ms)`, error?.message ?? '');
        }
        throw error;
      })
    );
  }
}

// Network-status / visibility / device-info / page-load logging removed.
// They ran on every load and produced 5+ lines of console noise per visit
// without ever helping debug a real problem. Re-add a focused logger if a
// specific question comes up.

// Sentry is loaded dynamically so the SDK is never bundled when sentryDsn is
// empty (UAT/dev). Static import was pulling ~250 KB into the main chunk
// unconditionally, causing Lighthouse perf 32/100 (LCP 8.2s, TBT 2.7s).
async function buildSentryProviders(): Promise<Provider[]> {
  if (!environment.sentryDsn) return [];
  const Sentry: typeof SentryType = await import('@sentry/angular');
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: (environment as { sentryEnvironment?: string }).sentryEnvironment ?? 'production',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 0,
  });
  return [
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    { provide: APP_INITIALIZER, useFactory: () => () => {}, deps: [Sentry.TraceService], multi: true }, // NOSONAR S1874
  ];
}

buildSentryProviders().then((sentryProviders) => {
  bootstrapApplication(AppComponent, {
    providers: [
      provideRouter(appRoutes, withPreloading(NoPreloading)),
      provideAnimations(), // NOSONAR S1874
      provideHttpClient(
        withInterceptors([authInterceptorFn]),
      ),
      providePrimeNG({
        theme: {
          preset: AlphaGrovesPreset,
          options: { darkModeSelector: '.dark' },
        },
      }),
      { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
      // Prefetch the public, read-only config (Analysis layout flag) at boot so
      // the right layout renders without a flash. load() never rejects, so a
      // failed fetch just leaves the safe default ('classic') in place.
      { provide: APP_INITIALIZER, useFactory: (pc: PublicConfigService) => () => pc.load(), deps: [PublicConfigService], multi: true }, // NOSONAR S1874
      ...sentryProviders,
    ],
  }).catch((err) => {
    console.error(`[${new Date().toISOString()}] 💥 Bootstrap Error`, err);
  });
});
